import React, { useEffect, useMemo, useState } from "react";
import { Rss, Plus, Trash2, RefreshCw, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCategories } from "@/context/CategoriesContext";
import { toast } from "sonner";

const inputCls =
  "w-full bg-black/40 border border-white/10 px-3.5 py-3 text-white placeholder-zinc-600 focus:outline-none focus:border-ec-volt transition-colors";

function fmt(iso) {
  return iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "never";
}

function FeedStatus({ f }) {
  const color = f.status === "failed" ? "text-ec-red" : f.status === "ok" ? "text-ec-volt" : "text-zinc-500";
  return (
    <div className="text-xs text-zinc-500 mt-2">
      <span className={color}>{f.status === "failed" ? "Failed" : f.status === "ok" ? "OK" : "Idle"}</span>
      {" · "}<Clock size={11} className="inline -mt-0.5" /> Last synced {fmt(f.last_run)}
      {f.status === "ok" && ` · +${f.last_added ?? 0} / ~${f.last_updated ?? 0} / −${f.last_removed ?? 0}`}
      {f.auto_daily && (
        <span className="block mt-0.5 text-zinc-600">Next auto-sync {f.next_sync_at ? fmt(f.next_sync_at) : "within the hour"}</span>
      )}
      {f.status === "failed" && f.last_error && <span className="text-ec-red block mt-1 break-words">{f.last_error}</span>}
    </div>
  );
}

// Self-service feed manager for the organiser portal.
export const OrganiserFeeds = ({ myCities = [], onSynced }) => {
  const { sports, teams: allOrgs } = useCategories();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const empty = { name: "", url: "", city: myCities[0]?.slug || "worcester", sport: "", team: "", venue: "", auto_daily: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const teams = useMemo(() => (form.sport ? allOrgs.filter((t) => t.sport === form.sport) : []), [form.sport, allOrgs]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/organiser/feeds").then(({ data }) => setFeeds(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!form.city && myCities.length) setForm((f) => ({ ...f, city: myCities[0].slug })); }, [myCities]); // eslint-disable-line

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.url || !form.sport || !form.city) { toast.error("Name, feed URL, category and city are required"); return; }
    setSaving(true);
    try {
      await api.post("/organiser/feeds", { ...form, team: form.team || null });
      toast.success("Feed added — hit Sync to import");
      setForm({ ...empty, city: form.city });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sync = async (id) => {
    setSyncing(id);
    try {
      const { data } = await api.post(`/organiser/feeds/${id}/sync`);
      toast.success(data.message);
      load(); onSynced?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSyncing(null); }
  };

  const toggleAuto = async (f) => {
    try { await api.patch(`/organiser/feeds/${f.id}`, { auto_daily: !f.auto_daily }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feed and all events it imported?")) return;
    try { await api.delete(`/organiser/feeds/${id}`); toast.success("Feed deleted"); load(); onSynced?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="bg-ec-surface border border-white/10 p-6 mb-10" data-testid="organiser-feeds">
      <h2 className="font-heading text-3xl tracking-wide flex items-center gap-2 mb-2"><Rss size={20} className="text-ec-volt" /> Calendar Feeds (auto-import)</h2>
      <p className="text-sm text-zinc-400 mb-5">Point us at your existing calendar (.ics) and we'll import fixtures automatically — no manual entry. Events publish straight away and stay in sync when you re-sync.</p>

      {loading ? <p className="overline text-zinc-500">Loading…</p> : feeds.length > 0 && (
        <div className="space-y-3 mb-6" data-testid="feeds-list">
          {feeds.map((f) => (
            <div key={f.id} data-testid={`feed-${f.id}`} className="border border-white/10 p-4 bg-black/30">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h4 className="font-heading text-xl tracking-wide leading-none">{f.name}</h4>
                  <a href={f.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-ec-volt underline break-all">{f.url}</a>
                  <FeedStatus f={f} />
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button data-testid={`feed-sync-${f.id}`} onClick={() => sync(f.id)} disabled={syncing === f.id} className="inline-flex items-center gap-1.5 overline bg-ec-volt text-black px-3 py-2 hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    <RefreshCw size={13} className={syncing === f.id ? "animate-spin" : ""} /> {syncing === f.id ? "Syncing…" : "Sync now"}
                  </button>
                  <div className="flex items-center gap-2">
                    <button data-testid={`feed-auto-${f.id}`} onClick={() => toggleAuto(f)} className={`overline px-2 py-1 border transition-colors ${f.auto_daily ? "bg-ec-volt/20 border-ec-volt text-ec-volt" : "border-white/15 text-zinc-500"}`}>
                      Daily {f.auto_daily ? "ON" : "OFF"}
                    </button>
                    <button data-testid={`feed-delete-${f.id}`} onClick={() => del(f.id)} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={add} className="space-y-4 border-t border-white/10 pt-5" data-testid="add-feed-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Feed name *</span>
            <input data-testid="feed-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="e.g. Warriors Fixtures" /></label>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">ICS feed URL *</span>
            <input data-testid="feed-url" type="url" value={form.url} onChange={set("url")} className={inputCls} placeholder="https://… .ics" /></label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="overline text-zinc-500 mb-2 block">City *</span>
            <select data-testid="feed-city" value={form.city} onChange={set("city")} className={inputCls}>
              {myCities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            </select></label>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Default category *</span>
            <select data-testid="feed-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value, team: "" })} className={inputCls}>
              <option value="">Select</option>{sports.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select></label>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Organiser / Team</span>
            <select data-testid="feed-team" value={form.team} onChange={set("team")} className={inputCls} disabled={!form.sport}>
              <option value="">Optional</option>{teams.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select></label>
          <label className="block"><span className="overline text-zinc-500 mb-2 block">Fallback venue</span>
            <input data-testid="feed-venue" value={form.venue} onChange={set("venue")} className={inputCls} placeholder="Used if the feed has no location" /></label>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input data-testid="feed-auto-daily" type="checkbox" checked={form.auto_daily} onChange={(e) => setForm({ ...form, auto_daily: e.target.checked })} className="accent-ec-volt w-4 h-4" />
          <span className="overline text-zinc-400">Auto-refresh daily</span>
        </label>
        <div>
          <button type="submit" disabled={saving} data-testid="feed-submit" className="inline-flex items-center gap-2 overline bg-ec-volt text-black px-6 py-3 hover:-translate-y-0.5 transition-all disabled:opacity-50">
            <Plus size={16} /> {saving ? "Adding…" : "Add Feed"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Admin oversight: all provider feeds across organisers, with sync + delete.
export const AdminProviderFeeds = () => {
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);

  const load = () => api.get("/admin/organiser-feeds").then(({ data }) => setFeeds(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const sync = async (id) => {
    setSyncing(id);
    try { const { data } = await api.post(`/admin/organiser-feeds/${id}/sync`); toast.success(data.message); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSyncing(null); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this feed and all events it imported?")) return;
    try { await api.delete(`/organiser/feeds/${id}`); toast.success("Feed deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading) return null;
  if (!feeds.length) return null;

  return (
    <div className="bg-ec-surface border border-white/10 p-6" data-testid="admin-provider-feeds">
      <h3 className="font-heading text-2xl tracking-wide flex items-center gap-2 mb-4"><Rss size={18} className="text-ec-volt" /> Provider Feeds</h3>
      <div className="space-y-3">
        {feeds.map((f) => (
          <div key={f.id} data-testid={`admin-feed-${f.id}`} className="border border-white/10 p-4 bg-black/30 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-heading text-xl tracking-wide leading-none">{f.name}</h4>
                <span className="overline bg-white/10 px-2 py-0.5 text-zinc-300">{f.organiser_name}</span>
                {f.auto_daily && <span className="overline text-ec-volt">Daily</span>}
              </div>
              <a href={f.url} target="_blank" rel="noreferrer" className="text-xs text-zinc-500 hover:text-ec-volt underline break-all">{f.url}</a>
              <FeedStatus f={f} />
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`admin-feed-sync-${f.id}`} onClick={() => sync(f.id)} disabled={syncing === f.id} className="inline-flex items-center gap-1.5 overline border border-white/15 px-3 py-2 hover:border-ec-volt hover:text-ec-volt transition-colors disabled:opacity-50">
                <RefreshCw size={13} className={syncing === f.id ? "animate-spin" : ""} /> {syncing === f.id ? "…" : "Sync"}
              </button>
              <button data-testid={`admin-feed-delete-${f.id}`} onClick={() => del(f.id)} className="text-zinc-500 hover:text-ec-red transition-colors"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
